import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import * as BackstageConstants from '../../../constants';
import * as D from '../../../deps';
import { getBackstageAnnotation } from '../../../utils';
import BackstageHeaderPlayPauseButton from '../../BackstageHeaderPlayPauseButton';
import BackstageLargeHeader from '../../BackstageLargeHeader';
import { withArtistButtonControls } from '../../common/ArtistButtonControls';
import ArtistSubtitleSmall from '../../common/ArtistSubtitleSmall';
import ListenerCountSubtitleLarge from '../../common/ListenerCountSubtitleLarge';
import LayoutManager from '../../layout/LayoutManager';

import ArtistBio from './ArtistBio';
import ArtistMainContent from './ArtistMainContent';
import ArtistSidebarButtons from './ArtistSidebarButtons';

const BackstageArtist = props => {
  const {
    pandoraId,
    pandoraType,
    name,
    bio,
    shareableUrlPath,
    stationListenerCount,
    albumCount,
    largeHeaderUrl,
    playTopSongs,
    pauseTopSongs,
    isArtistTopSongsPlaying,
    topSongsLooksDisabled,
    topSongsIsDisabled,
    topSongsHaveNoRights,
    hasRadio,
    playArtistStation,
    shareArtist,
    isMobileDevice,
    largeHeaderDominantColor,
    graphQLRequest,
    isVariousArtists,
    hasDetails,
    hasCollaborationArtists,
    isCuratorExperienceEnabled,
  } = props;

  const [isLoading, setIsLoading] = useState(false);

  function fetchArtistCollaborations() {
    const shouldFetch =
      !isVariousArtists &&
      !hasCollaborationArtists &&
      pandoraType === D.Constants.SourceTypes.PandoraTypes.ARTIST;
    if (shouldFetch) {
      const requestParams = D.GraphQL.Queries.getArtistCollaborations({
        pandoraId,
      });
      graphQLRequest(requestParams);
    }
  }

  useEffect(() => {
    if (!hasDetails) {
      const requestParams =
        pandoraType === D.Constants.SourceTypes.PandoraTypes.COMPOSER
          ? D.GraphQL.Queries.getComposerDetails({ pandoraId })
          : D.GraphQL.Queries.getArtistDetails({
              pandoraId,
              withCollaborations: true,
            });

      setIsLoading(true);
      fetchArtistCollaborations();
      graphQLRequest(requestParams).then(() => {
        setIsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pandoraId]);

  const iconType = isArtistTopSongsPlaying ? 'pause' : 'play';
  const onClick = isArtistTopSongsPlaying ? pauseTopSongs : playTopSongs;

  const LargeHeaderButton = largeHeaderUrl ? (
    <BackstageHeaderPlayPauseButton
      theme={D.ButtonT3Theme.OPAQUE_WHITE}
      isPillShaped={true}
      isDisabled={topSongsIsDisabled}
      looksDisabled={topSongsLooksDisabled}
      iconType={iconType}
      onClick={onClick}
    />
  ) : null;

  const LargeHeader = largeHeaderUrl ? (
    <BackstageLargeHeader
      listenerCount={<ListenerCountSubtitleLarge stationListenerCount={stationListenerCount} />}
      name={name}
      dominantColor={largeHeaderDominantColor}
      useLightTheme={!D.Utils.Art.isColorDark(largeHeaderDominantColor)}
      largeHeaderImageUrl={largeHeaderUrl}
      playPauseButton={LargeHeaderButton}
    />
  ) : null;

  const Sidebar = !isMobileDevice ? (
    <ArtistSidebarButtons
      playTopSongs={playTopSongs}
      pauseTopSongs={pauseTopSongs}
      isArtistTopSongsPlaying={isArtistTopSongsPlaying}
      playArtistStation={playArtistStation}
      shareArtist={shareArtist}
      topSongsHaveNoRights={topSongsHaveNoRights}
      hasRadio={hasRadio}
    />
  ) : null;

  const Bio = (
    <ArtistBio
      pandoraId={pandoraId}
      shareableUrlPath={shareableUrlPath}
      isMobileDevice={isMobileDevice}
      shortTextWidthRatio={largeHeaderUrl || isMobileDevice ? 4 : null}
      bio={bio}
    />
  );

  return (
    <LayoutManager
      pandoraId={pandoraId}
      subTitleLrg={<ListenerCountSubtitleLarge stationListenerCount={stationListenerCount} />}
      subTitleSm={<ArtistSubtitleSmall albumCount={albumCount} />}
      mainContent={
        <ArtistMainContent
          pandoraId={pandoraId}
          bio={LargeHeader ? null : Bio}
          isLoading={isLoading}
        />
      }
      sidebarButtons={Sidebar}
      largeHeader={LargeHeader}
      bio={LargeHeader ? Bio : null}
      isMobileDevice={isMobileDevice}
      isHeaderInSidebar={isCuratorExperienceEnabled}
    />
  );
};

BackstageArtist.propTypes = {
  params: PropTypes.shape({
    pandoraId: PropTypes.string.isRequired,
  }),
  pandoraId: PropTypes.string,
  pandoraType: PropTypes.string,
  graphQLRequest: PropTypes.func,
  shareableUrlPath: PropTypes.string,
  largeHeaderUrl: PropTypes.string,
  largeHeaderDominantColor: PropTypes.string,
  name: PropTypes.string,
  bio: PropTypes.string,
  albumCount: PropTypes.number,
  stationListenerCount: PropTypes.number,
  playTopSongs: PropTypes.func,
  pauseTopSongs: PropTypes.func,
  isArtistTopSongsPlaying: PropTypes.bool,
  topSongsLooksDisabled: PropTypes.bool,
  topSongsIsDisabled: PropTypes.bool,
  playArtistStation: PropTypes.func,
  shareArtist: PropTypes.func,
  hasRadio: PropTypes.bool,
  isMobileDevice: PropTypes.bool,
  topSongsHaveNoRights: PropTypes.bool,
  isVariousArtists: PropTypes.bool,
  hasDetails: PropTypes.bool,
  hasCollaborationArtists: PropTypes.bool,
  isCuratorExperienceEnabled: PropTypes.bool,
};

export function mapStateToProps(state, ownProps) {
  const encryptedId = get(ownProps, ['params', 'pandoraId'], null);
  const annotation = getBackstageAnnotation(state, encryptedId) || BackstageConstants.emptyMap;
  const pandoraId = annotation.get('pandoraId', null);
  const pandoraType = annotation.get('type', D.Constants.SourceTypes.PandoraTypes.ARTIST);
  const details = D.getInDomain('catalog.utils.getCatalogDetailsFromState')(state, pandoraId);

  return {
    pandoraId,
    pandoraType,
    name: annotation.get('name', ''),
    shareableUrlPath: annotation.get('shareableUrlPath', ''),
    albumCount: details.get('albumCount', 0),
    bio: details.get('bio', ''),
    stationListenerCount: details.get('stationListenerCount', 0),
    largeHeaderUrl: D.Utils.Art.getLargeHeader(state, details),
    largeHeaderDominantColor: details.getIn(['largeHeaderImage', 'dominantColor']),
    isMobileDevice: D.Utils.Capabilities.isMobileDevice(state),
    isVariousArtists: D.getInDomain('catalog.utils.isVariousArtists')(annotation),
    hasDetails: details.size > 0,
    hasCollaborationArtists: details.get('collaborationArtists') ? true : false,
    isCuratorExperienceEnabled: D.Utils.TunerVars.curatorExperienceEnabled(state),
  };
}

const mapDispatchToProps = {
  graphQLRequest: D.Services.GraphQL.graphQLRequest,
};

const enhance = compose(withArtistButtonControls, connect(mapStateToProps, mapDispatchToProps));

export default enhance(BackstageArtist);
